import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight, Send, ShieldAlert, CheckCircle2, AlertTriangle, XCircle, Plus } from "lucide-react";
import { api, errMsg, fileUrl, eur, computeDue, lineTotals } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, LinesEditor, Select, Spinner, cleanLines, validLines, emptyLine } from "@/components/common";
import { SupplierForm } from "@/pages/Suppliers";
import Modal from "@/components/Modal";

const dot = (c) => (c >= 0.85 ? "bg-[#2E7D5B]" : c >= 0.5 ? "bg-[#A5894E]" : "bg-[#B03A3A]");
const confLabel = (c) => (c >= 0.85 ? "Fiable" : c >= 0.5 ? "À vérifier" : "Non trouvé");
const ICON = { ok: [CheckCircle2, "text-[#2E7D5B]"], warn: [AlertTriangle, "text-[#8A6F3A]"], error: [XCircle, "text-[#B03A3A]"] };

function Viewer({ fileId, pages, highlight }) {
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  useEffect(() => { if (highlight?.page) setPage(highlight.page); }, [highlight]);
  const box = highlight?.box && (highlight.page || 1) === page ? highlight.box : null;
  return (
    <div className="card flex h-full flex-col overflow-hidden" data-testid="scan-viewer">
      <div className="flex items-center gap-1 border-b border-[#E4E4EC] p-2">
        <button className="btn-ghost" onClick={() => setZoom(Math.max(0.5, zoom - 0.25))} data-testid="viewer-zoom-out"><ZoomOut size={16} /></button>
        <span className="num w-12 text-center text-xs">{Math.round(zoom * 100)} %</span>
        <button className="btn-ghost" onClick={() => setZoom(Math.min(3, zoom + 0.25))} data-testid="viewer-zoom-in"><ZoomIn size={16} /></button>
        <button className="btn-ghost" onClick={() => setRot((rot + 90) % 360)} data-testid="viewer-rotate"><RotateCw size={16} /></button>
        <div className="ml-auto flex items-center gap-1">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="viewer-prev-page"><ChevronLeft size={16} /></button>
          <span className="text-xs" data-testid="viewer-page-indicator">Page {page} / {pages}</span>
          <button className="btn-ghost" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="viewer-next-page"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-[#E4E4EC]/60 p-4">
        <div className="relative mx-auto origin-top transition-transform" style={{ width: `${zoom * 100}%`, transform: `rotate(${rot}deg)` }}>
          <img src={fileUrl(fileId, page)} alt="Scan de la facture" className="w-full bg-white shadow" data-testid="viewer-image" />
          {box && <div className="pointer-events-none absolute rounded-sm border-2 border-[#A5894E] bg-[#A5894E]/25 transition-all" data-testid="viewer-highlight"
            style={{ top: `${box[0] / 10}%`, left: `${box[1] / 10}%`, height: `${Math.max(1.2, (box[2] - box[0]) / 10)}%`, width: `${(box[3] - box[1]) / 10}%` }} />}
        </div>
      </div>
    </div>
  );
}

const FIELD_LABELS = { supplier_name: "Fournisseur lu", siret: "SIRET lu", vat_number: "TVA lue", invoice_number: "N° de facture",
  invoice_date: "Date de facture", due_date: "Date d'échéance", order_reference: "Référence de commande", total_ht: "Total HT lu", total_vat: "TVA lue (montant)", total_ttc: "Total TTC lu" };

export default function ScanVerify() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useAuth();
  const [scan, setScan] = useState(null);
  const [pages, setPages] = useState(1);
  const [sups, setSups] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(null);
  const [checks, setChecks] = useState(null);
  const [active, setActive] = useState(null);
  const [newSup, setNewSup] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef();

  const loadSups = () => api.get("/suppliers").then((r) => setSups(r.data));
  useEffect(() => {
    api.get(`/scans/${id}`).then((r) => {
      const s = r.data;
      setScan(s);
      const v = (k) => s.fields?.[k]?.value ?? "";
      setForm({ supplier_id: s.checks?.supplier_id || "", number: v("invoice_number"), date: v("invoice_date"), due_date: v("due_date"), order_id: s.checks?.order_id || "",
        cost_center: "", siret: v("siret"), vat_number: v("vat_number"), supplier_name: v("supplier_name"), order_reference: v("order_reference"),
        total_ht: v("total_ht"), total_vat: v("total_vat"), total_ttc: v("total_ttc"),
        lines: (s.lines?.length ? s.lines : [emptyLine()]).map((l) => ({ category: "materiel", reference: "", ...l })) });
      setChecks(s.checks);
      api.get(`/files/${s.pdf_file_id || s.original_file_id}/meta`).then((m) => setPages(m.data.pages));
    });
    loadSups();
    api.get("/orders").then((r) => setOrders(r.data));
  }, [id]);

  const sup = sups.find((s) => s.id === form?.supplier_id);
  useEffect(() => {
    if (!form || !sup) return;
    setForm((f) => ({ ...f, lines: f.lines.map((l) => ({ ...l, category: l._cat ? l.category : sup.category })) }));
  }, [sup?.id]); // eslint-disable-line

  const recheck = useCallback((f) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const s = sups.find((x) => x.id === f.supplier_id);
      api.post("/scans/check", { fields: { supplier_name: s?.name || f.supplier_name, siret: s?.siret || f.siret, vat_number: s ? s.vat_number : f.vat_number,
        invoice_number: f.number, invoice_date: f.date, order_reference: orders.find((o) => o.id === f.order_id)?.number || f.order_reference,
        total_ht: f.total_ht, total_vat: f.total_vat, total_ttc: f.total_ttc }, lines: cleanLines(f.lines) }).then((r) => setChecks(r.data));
    }, 400);
  }, [sups, orders]);
  const set = (k, v) => setForm((f) => { const n = { ...f, [k]: v }; if (k === "date" && sup) n.due_date = computeDue(v, sup.payment_terms, settings?.payment_terms); recheck(n); return n; });

  const totals = useMemo(() => lineTotals(form?.lines || []), [form?.lines]);
  if (!scan || !form) return <Spinner />;
  const conf = (k) => scan.fields?.[k]?.confidence ?? 0;
  const focus = (k) => setActive(scan.fields?.[k] ? { ...scan.fields[k], key: k } : null);
  const Dot = ({ k }) => <span title={confLabel(conf(k))} data-testid={`confidence-${k}`} className={`ml-2 inline-block h-2.5 w-2.5 rounded-full align-middle ${dot(conf(k))}`} />;
  const blocking = checks?.messages?.some((m) => m.level === "error");
  const ok = form.supplier_id && form.number && form.date && form.due_date && form.cost_center && validLines(form.lines) && !blocking;

  const submit = async (transmit) => {
    setBusy(true);
    try {
      const r = await api.post(`/scans/${id}/submit`, { ...form, lines: cleanLines(form.lines), order_id: form.order_id || "", transmit });
      if (r.data.matching?.warning) toast.warning(`Écart de rapprochement de ${String(r.data.matching.gap_pct).replace(".", ",")} % avec ${r.data.matching.order_number}`);
      toast.success(transmit ? "Facture validée et transmise à la comptabilité" : "Facture enregistrée en brouillon");
      nav(`/factures/${r.data.invoice.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const inp = (k, fk, type = "text") => (
    <Field label={<>{FIELD_LABELS[fk]}<Dot k={fk} /></>}>
      <input type={type} step="0.01" className="inp" value={form[k] ?? ""} onFocus={() => focus(fk)} onChange={(e) => set(k, e.target.value)} data-testid={`verify-${k}-input`} />
    </Field>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button className="btn-ghost" onClick={() => nav("/factures-a-traiter")} data-testid="verify-back-btn"><ChevronLeft size={16} /> Factures à traiter</button>
        <h1 className="text-xl font-extrabold">Vérification · <span className="font-semibold text-[#6B6B80]">{scan.filename}</span></h1>
        {scan.kind === "facturx" && <span className="rounded-full bg-[#16163F] px-2.5 py-0.5 text-[11px] font-bold text-white">Factur-X (XML)</span>}
        {scan.sender && <span className="text-xs text-[#6B6B80]">Reçue de {scan.sender} – « {scan.subject} »</span>}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="xl:sticky xl:top-20 xl:h-[calc(100vh-7rem)]"><Viewer fileId={scan.pdf_file_id || scan.original_file_id} pages={pages} highlight={active} /></div>
        <div className="space-y-5">
          {blocking && <div className="flex gap-3 rounded-lg border-2 border-[#B03A3A] bg-[#B03A3A]/10 p-4 text-sm font-semibold text-[#B03A3A]" data-testid="verify-blocking-alert"><ShieldAlert className="shrink-0" />
            <div>{checks.messages.filter((m) => m.level === "error").map((m, i) => <p key={i}>{m.text}</p>)}</div></div>}
          <div className="card p-5" data-testid="verify-checks">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6B6B80]">Contrôles automatiques</h2>
            <ul className="space-y-1.5">{checks?.messages?.map((m, i) => { const [I, c] = ICON[m.level]; return <li key={i} className="flex items-start gap-2 text-sm"><I size={16} className={`mt-0.5 shrink-0 ${c}`} />{m.text}</li>; })}</ul>
            <p className="mt-3 flex gap-4 text-[11px] text-[#6B6B80]"><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-[#2E7D5B]" />Fiable</span><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-[#A5894E]" />À vérifier</span><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-[#B03A3A]" />Non trouvé</span> · cliquez sur un champ pour le situer sur le scan</p>
          </div>
          <div className="card space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={<>Fournisseur<Dot k="supplier_name" /></>}>
                <div className="flex gap-2">
                  <Select value={form.supplier_id} onChange={(v) => set("supplier_id", v)} placeholder="— Choisir —" options={sups.map((s) => ({ value: s.id, label: s.name }))} testid="verify-supplier-select" />
                  <button type="button" className="btn-ghost shrink-0" title="Créer le fournisseur" onClick={() => setNewSup(true)} data-testid="verify-create-supplier-btn"><Plus size={16} /></button>
                </div>
                <span className="mt-1 block text-xs text-[#6B6B80]" onClick={() => focus("supplier_name")}>Lu : {form.supplier_name || "—"} · SIRET {form.siret || "—"}</span>
              </Field>
              {inp("number", "invoice_number")}
              {inp("date", "invoice_date", "date")}
              {inp("due_date", "due_date", "date")}
              <Field label={<>Bon de commande<Dot k="order_reference" /></>}>
                <Select value={form.order_id} onChange={(v) => set("order_id", v)} placeholder="— Aucun —" testid="verify-order-select"
                  options={orders.filter((o) => !form.supplier_id || o.supplier_id === form.supplier_id).map((o) => ({ value: o.id, label: `${o.number} – ${eur(o.total_ht)} HT` }))} />
              </Field>
              <Field label="Centre de coût">
                <Select value={form.cost_center} onChange={(v) => set("cost_center", v)} placeholder="— Choisir —" options={(settings?.my_cost_centers || []).map((c) => ({ value: c.code, label: `${c.code} – ${c.label}` }))} testid="verify-cost-center-select" />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">{inp("total_ht", "total_ht", "number")}{inp("total_vat", "total_vat", "number")}{inp("total_ttc", "total_ttc", "number")}</div>
            <LinesEditor lines={form.lines} onChange={(l) => set("lines", l.map((x) => ({ ...x, _cat: true })))} testid="verify-lines" />
            {Number(form.total_ttc) > 0 && Math.abs(totals.ttc - Number(form.total_ttc)) > 0.05 && <p className="text-xs font-semibold text-[#B03A3A]" data-testid="verify-total-mismatch">Le total TTC des lignes ({eur(totals.ttc)}) diffère du total lu sur la facture ({eur(form.total_ttc)}).</p>}
            <div className="flex flex-wrap justify-end gap-2 border-t border-[#E4E4EC] pt-4">
              <button className="btn-secondary" disabled={!ok || busy} onClick={() => submit(false)} data-testid="verify-draft-btn">Enregistrer en brouillon</button>
              <button className="btn-primary" disabled={!ok || busy} onClick={() => submit(true)} data-testid="verify-submit-btn"><Send size={16} /> Valider et transmettre à la comptabilité</button>
            </div>
          </div>
        </div>
      </div>
      <Modal open={newSup} onClose={() => setNewSup(false)} title="Créer le fournisseur" wide testid="verify-new-supplier-modal">
        <SupplierForm initial={{ name: form.supplier_name, siret: (form.siret || "").replace(/\s/g, ""), vat_number: form.vat_number }}
          onSaved={(s) => { setNewSup(false); loadSups().then(() => set("supplier_id", s.id)); }} />
      </Modal>
    </div>
  );
}
